import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/common/Button';
import { codiceInvito, guastoScritturaOf, leggiCodiceInvito, POSTI_GIOCA, POSTI_GIOCA_2V2, POSTI_INVITO } from '@/lib/burraco/scritture';
import { useAzioneBurraco, type GuastoAzione } from '@/lib/burraco/useAzioneBurraco';
import { sospensioneDi, type Sospensione } from '@/lib/burraco/condotta';
import { formatData, formatOra } from '@/lib/fmt';
import { useLinguaFormato } from '@/hooks/useLinguaFormato';
import { useCreateBurracoTableMutation, useJoinBurracoTableMutation, useLazyGetMyBurracoCondottaQuery } from '@/services/limuloApi';

type Azione = 'gioca' | 'gioca2v2' | 'invita' | 'entra';

/** La frase di un guasto: una chiave di una lista chiusa sotto `hub.gioca.guasto.`. Il testo del server non si mostra MAI. */
function chiaveGuasto(azione: Azione, g: GuastoAzione | 'codiceNonValido'): string {
  switch (g) {
    case 'non-disponibile':
      return 'nonDisponibile';
    case 'conflitto':
      return azione === 'entra' ? 'conflittoEntra' : 'conflittoCrea';
    case 'nonTrovato':
      return azione === 'entra' ? 'nonTrovato' : 'errore';
    default:
      return g;
  }
}

/**
 * «GIOCA», «INVITA UN AMICO» ed «ENTRA CON UN CODICE» nella Hub del Burraco (PR2 dell'ingresso; condizioni Z8-Z10 di tma-orcb): le tre SCRITTURE, tutte con la chiave di idempotenza del
 * `useAzioneBurraco` (il doppio tocco non crea due tavoli). Le risposte sono IPOTESI (nessuno ha misurato una POST: la prima prova la fa tma-orcb dal vivo, Z9).
 *  · «Gioca 1 contro 1» e «Gioca 2 contro 2»: un tavolo casual a 2 o a 4 posti (`burraco.table.create`; Luca 12/10); i posti liberi li riempiono i robot del game host (il client
 *    non sceglie il loro livello); apre `/burraco/tavolo/:id`. Due azioni, due chiavi di idempotenza: un tocco sull'una non ripete l'altra.
 *  · «Invita un amico»: un tavolo privato; mostra il CODICE da condividere (`tableId` + invito) e NON entra da solo: l'invito si vede una volta sola, e chi se ne va senza copiarlo lo perde.
 *  · «Entra con un codice»: `burraco.table.join` col `tableId` e l'invito letti dal codice; poi apre il tavolo. Un codice che non ha la forma non parte nemmeno.
 * Il tavolo di GRUPPO non c'e (non e servito). Il codice e una credenziale: sta SOLO nello stato del componente, mai in storage, in log o nell'URL.
 */
/** `inTitolo`: cio` che sta a destra del titolo (l'icona «Come si gioca», che monta la Hub). */
export function GiocaBurraco({ inTitolo }: { inTitolo?: ReactNode } = {}) {
  const { t } = useTranslation('burraco');
  const navigate = useNavigate();
  const titoloId = useId();
  const [crea] = useCreateBurracoTableMutation();
  const [entra] = useJoinBurracoTableMutation();
  const [leggiCondotta] = useLazyGetMyBurracoCondottaQuery();
  const aGioca = useAzioneBurraco<{ tableId: string }>();
  const aGioca2v2 = useAzioneBurraco<{ tableId: string }>();
  const aInvita = useAzioneBurraco<{ tableId: string; codice: string | null }>();
  const aEntra = useAzioneBurraco<{ tableId: string }>();
  const [invitoPronto, setInvitoPronto] = useState<{ tableId: string; codice: string | null } | null>(null);
  const [testo, setTesto] = useState('');
  const [codiceStorto, setCodiceStorto] = useState(false);
  const [ultima, setUltima] = useState<Azione | null>(null);
  // I-762: il 403 di un tavolo `casual` e muto (il BFF non dice il motivo): dopo, UNA rilettura della condotta; se dice `sospensione` la frase lo spiega, altrimenti resta `vietato`
  const [sospensione, setSospensione] = useState<Sospensione | null>(null);
  const occupato = aGioca.stato.invio || aGioca2v2.stato.invio || aInvita.stato.invio || aEntra.stato.invio;

  const vaiAlTavolo = (tableId: string) => navigate(`/burraco/tavolo/${tableId}`);

  /** UNA lettura per rifiuto, mai un giro: se non risponde, non e `sospensione` o la forma non si legge, non c'e frase nuova e resta `vietato`. */
  const rileggiLaCondotta = async () => {
    try {
      const condotta = await leggiCondotta(undefined, false).unwrap();
      setSospensione(sospensioneDi(condotta, Date.now()));
    } catch {
      setSospensione(null);
    }
  };

  const gioca = async (quale: 'gioca' | 'gioca2v2') => {
    setUltima(quale);
    setInvitoPronto(null);
    setSospensione(null);
    const azione = quale === 'gioca' ? aGioca : aGioca2v2;
    const posti = quale === 'gioca' ? POSTI_GIOCA : POSTI_GIOCA_2V2;
    const r = await azione.esegui(async (idempotencyKey) => {
      try {
        const tavolo = await crea({ tipo: 'casual', posti, idempotencyKey }).unwrap();
        return tavolo === null ? null : { tableId: tavolo.tableId };
      } catch (e) {
        // il privato non conta per la condotta e non rilegge; il casual con un 403 si
        if (guastoScritturaOf(e) === 'vietato') await rileggiLaCondotta();
        throw e;
      }
    });
    if (r !== null) vaiAlTavolo(r.tableId);
  };

  const invita = async () => {
    setUltima('invita');
    setInvitoPronto(null);
    setSospensione(null);
    const r = await aInvita.esegui(async (idempotencyKey) => {
      const tavolo = await crea({ tipo: 'privato', posti: POSTI_INVITO, idempotencyKey }).unwrap();
      return tavolo === null ? null : { tableId: tavolo.tableId, codice: tavolo.invito === null ? null : codiceInvito(tavolo.tableId, tavolo.invito) };
    });
    if (r !== null) setInvitoPronto(r);
  };

  const entraCol = async (e: FormEvent) => {
    e.preventDefault();
    setUltima('entra');
    setInvitoPronto(null);
    setSospensione(null);
    const letto = leggiCodiceInvito(testo);
    setCodiceStorto(letto === null);
    if (letto === null) return; // niente richiesta per un codice che non ha la forma
    const r = await aEntra.esegui(async (idempotencyKey) => {
      const ingresso = await entra({ tableId: letto.tableId, invito: letto.invito, idempotencyKey }).unwrap();
      return ingresso === null ? null : { tableId: ingresso.tableId };
    });
    if (r !== null) {
      setTesto('');
      vaiAlTavolo(r.tableId);
    }
  };

  const guasto =
    ultima === 'entra' && codiceStorto
      ? 'codiceNonValido'
      : ultima === 'gioca'
        ? aGioca.stato.guasto
        : ultima === 'gioca2v2'
          ? aGioca2v2.stato.guasto
          : ultima === 'invita'
            ? aInvita.stato.guasto
            : ultima === 'entra'
              ? aEntra.stato.guasto
              : null;

  return (
    <section data-testid="burraco-gioca" aria-labelledby={titoloId} className="flex shrink-0 flex-col gap-3">
      {/* `pr-2`: la radice della Hub (`overflow-y-auto`, quindi anche `overflow-x`) ritaglia la sua area di tocco: l'icona (32 px) ha un `after:` di 44 px che sporge di 6 px per lato, e a filo del bordo destro gli ultimi 6 px non ricevevano il tocco (banco 28/10: a ±21,5 px a destra elementFromPoint dava il contenitore). */}
      <div className="flex items-center justify-between gap-2 pr-2">
        <h2 id={titoloId} className="text-xs font-bold uppercase tracking-wide text-fg-muted">
          {t('hub.gioca.titolo')}
        </h2>
        {inTitolo}
      </div>
      <div className="flex flex-col gap-1">
        <Button variant="primary" fullWidth data-azione="gioca" disabled={occupato} onClick={() => void gioca('gioca')}>
          {aGioca.stato.invio ? t('hub.gioca.invio') : t('hub.gioca.gioca')}
        </Button>
        <p className="px-1 text-xs text-fg-muted">{t('hub.gioca.giocaNota')}</p>
      </div>
      <div className="flex flex-col gap-1">
        <Button variant="primary" fullWidth data-azione="gioca-2v2" disabled={occupato} onClick={() => void gioca('gioca2v2')}>
          {aGioca2v2.stato.invio ? t('hub.gioca.invio') : t('hub.gioca.gioca2v2')}
        </Button>
        <p className="px-1 text-xs text-fg-muted">{t('hub.gioca.gioca2v2Nota')}</p>
      </div>
      <div className="flex flex-col gap-1">
        <Button variant="secondary" fullWidth data-azione="invita" disabled={occupato} onClick={() => void invita()}>
          {aInvita.stato.invio ? t('hub.gioca.invio') : t('hub.gioca.invita')}
        </Button>
        <p className="px-1 text-xs text-fg-muted">{t('hub.gioca.invitaNota')}</p>
      </div>
      {invitoPronto !== null && <InvitoPronto pronto={invitoPronto} onVai={() => vaiAlTavolo(invitoPronto.tableId)} />}
      <form data-azione="entra" onSubmit={(e) => void entraCol(e)} className="flex flex-col gap-2">
        <label htmlFor={`${titoloId}-codice`} className="text-sm font-semibold text-fg">
          {t('hub.gioca.entra.titolo')}
        </label>
        <input
          id={`${titoloId}-codice`}
          type="text"
          value={testo}
          onChange={(e) => {
            setTesto(e.target.value);
            setCodiceStorto(false);
          }}
          placeholder={t('hub.gioca.entra.segnaposto')}
          autoCapitalize="none"
          autoCorrect="off"
          autoComplete="off"
          spellCheck={false}
          disabled={occupato}
          className="min-h-11 w-full min-w-0 rounded-xl border border-border bg-bg-secondary px-3 text-sm text-fg placeholder:text-fg-muted"
        />
        <Button type="submit" variant="secondary" fullWidth disabled={occupato || testo.trim() === ''}>
          {aEntra.stato.invio ? t('hub.gioca.invio') : t('hub.gioca.entra.azione')}
        </Button>
      </form>
      {guasto !== null &&
        (guasto === 'vietato' && sospensione !== null && (ultima === 'gioca' || ultima === 'gioca2v2') ? (
          <FraseSospensione sospensione={sospensione} />
        ) : (
          <p role="status" data-gioca-guasto={guasto} className="rounded-xl bg-bg-secondary px-4 py-3 text-sm text-danger">
            {t(`hub.gioca.guasto.${chiaveGuasto(ultima ?? 'gioca', guasto)}`)}
          </p>
        ))}
    </section>
  );
}

/**
 * La frase di SOSPENSIONE (I-762): «Sei sospeso dai tavoli in classifica <motivo> [fino alle HH:MM]. [N abbandoni negli ultimi 7 giorni.] [N turni scaduti negli ultimi 7 giorni.] Puoi giocare fuori classifica: «Invita un amico».»
 * Il MOTIVO e quello che il server conta (`abbandoni`, `turniScaduti`): «per abbandoni», «per turni scaduti», «per abbandoni e turni scaduti», e solo se nessuno dei due si legge «per condotta».
 * Ogni pezzo c'e SOLO se la condotta l'ha detto: l'ora (nel fuso del telefono, con la data se non e oggi) e i conti non si inventano mai.
 */
function FraseSospensione({ sospensione }: { sospensione: Sospensione }) {
  const { t } = useTranslation('burraco');
  const lingua = useLinguaFormato();
  const { fino, finoOggi, abbandoni, turniScaduti, motivo: causa } = sospensione;
  const ora = fino === null ? '' : formatOra(fino, lingua);
  const motivo = t(`hub.gioca.guasto.sospesoMotivo.${causa}`);
  const apertura =
    fino === null
      ? t('hub.gioca.guasto.sospeso', { motivo })
      : finoOggi
        ? t('hub.gioca.guasto.sospesoFino', { ora, motivo })
        : t('hub.gioca.guasto.sospesoFinoData', { ora, motivo, data: formatData(fino, { day: 'numeric', month: 'long' }, lingua) });
  return (
    <p role="status" data-gioca-guasto="sospeso" className="rounded-xl bg-bg-secondary px-4 py-3 text-sm text-danger">
      {apertura}
      {abbandoni !== null && <> {t('hub.gioca.guasto.sospesoAbbandoni', { count: abbandoni })}</>}
      {turniScaduti !== null && <> {t('hub.gioca.guasto.sospesoTurni', { count: turniScaduti })}</>} {t('hub.gioca.guasto.sospesoFuori', { invita: t('hub.gioca.invita') })}
    </p>
  );
}

/** Il codice da condividere, copiabile. «Copiato» si dice SOLO se la copia e riuscita (`navigator.clipboard.writeText` rigetta davvero); a copia fallita il codice si seleziona con un tocco lungo. */
function InvitoPronto({ pronto, onVai }: { pronto: { tableId: string; codice: string | null }; onVai: () => void }) {
  const { t } = useTranslation('burraco');
  const [esito, setEsito] = useState<'ok' | 'ko' | null>(null);
  useEffect(() => setEsito(null), [pronto.codice]);
  const copia = async () => {
    if (pronto.codice === null) return;
    try {
      await navigator.clipboard.writeText(pronto.codice);
      setEsito('ok');
    } catch {
      setEsito('ko');
    }
  };
  return (
    <div data-invito-pronto className="card-edge flex flex-col gap-2 rounded-xl bg-bg-secondary p-3">
      {pronto.codice === null ? (
        <p data-invito-assente className="text-sm text-fg-muted">
          {t('hub.gioca.codice.assente')}
        </p>
      ) : (
        <>
          <p className="text-sm font-semibold text-fg">{t('hub.gioca.codice.titolo')}</p>
          <code data-invito-codice className="select-all break-all rounded bg-bg-tertiary px-2 py-2 font-mono text-xs text-fg">
            {pronto.codice}
          </code>
          <p className="text-xs text-fg-muted">{t('hub.gioca.codice.avviso')}</p>
          <Button variant="secondary" fullWidth data-azione="copia" onClick={() => void copia()}>
            {esito === 'ok' ? t('hub.gioca.codice.copiato') : t('hub.gioca.codice.copia')}
          </Button>
          <p role="status" aria-live="polite" className={esito === 'ko' ? 'text-xs text-fg-muted' : 'sr-only'}>
            {esito === 'ko' ? t('hub.gioca.codice.copiaFallita') : esito === 'ok' ? t('hub.gioca.codice.copiato') : ''}
          </p>
        </>
      )}
      <Button variant="primary" fullWidth data-azione="vai" onClick={onVai}>
        {t('hub.gioca.codice.vai')}
      </Button>
    </div>
  );
}
